import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import 'highlight.js/styles/github-dark.css';
import { useTheme } from '../../contexts/ThemeContext';
import MermaidBlock from '../conversation/MermaidBlock';

// LLMs frequently emit bare mermaid diagrams without ```mermaid fences
// (especially when generate_mermaid_diagram → create_artifacts hands the
// raw diagram source straight into the artifact's content). Without
// fences, ReactMarkdown sees plain text and we render the source instead
// of the diagram. Auto-wrap any line starting with a known mermaid type
// keyword so the renderer's `code` component can claim the block and
// hand it to MermaidBlock. Mirrors the same preprocessing in
// MessageBubble.tsx — would be worth extracting to a shared util on
// the next pass.
const MERMAID_TYPE_STARTERS = [
  'pie ',
  'pie\n',
  'gantt',
  'flowchart ',
  'flowchart\n',
  'sequenceDiagram',
  'classDiagram',
  'graph ',
  'graph\n',
  'timeline',
  'erDiagram',
  'xychart',
  'stateDiagram',
  'mindmap',
  'journey',
  'requirementDiagram',
  'C4Context',
];

function autoFenceMermaid(content: string): string {
  try {
    const lines = content.split('\n');
    const out: string[] = [];
    let inMermaid = false;

    for (let i = 0; i < lines.length; i++) {
      const trimmed = lines[i].trim();
      const prevFenced =
        i > 0 && out[out.length - 1]?.trim().startsWith('```');

      if (
        !inMermaid &&
        !prevFenced &&
        MERMAID_TYPE_STARTERS.some((t) => trimmed.startsWith(t))
      ) {
        const prevNonEmpty =
          out.filter((l) => l.trim()).slice(-1)[0]?.trim() || '';
        if (!prevNonEmpty.startsWith('```')) {
          out.push('```mermaid');
          inMermaid = true;
        }
      }

      out.push(lines[i]);

      if (inMermaid && i < lines.length - 1) {
        const next = lines[i + 1] ?? '';
        const nextTrimmed = next.trim();
        const looksLikeMermaid =
          nextTrimmed === '' ||
          /^[\s]/.test(next) ||
          /^(title|section|dateFormat|axisFormat|todayMarker|\w+\s*:|\"\w|"\w|x-axis|y-axis|bar |line )/.test(nextTrimmed);
        const looksLikeMarkdown =
          /^[#*\-⚠🔴🎯📊📋⚡🔍❌✅>]/.test(nextTrimmed) ||
          /^[A-Z][a-z]+ [A-Z]/.test(nextTrimmed);
        if (looksLikeMarkdown && !looksLikeMermaid) {
          out.push('```');
          inMermaid = false;
        }
      }
    }
    if (inMermaid) out.push('```');
    return out.join('\n');
  } catch {
    // Defensive: any preprocessing crash falls back to the original
    // content so we never break rendering on a regex edge case.
    return content;
  }
}

export type MarkdownSize = 'small' | 'medium' | 'large';

interface ArtifactoryMarkdownProps {
  content: string;
  size?: MarkdownSize;
  className?: string;
  /**
   * Rehype plugins to append AFTER the built-in rehype-highlight.
   * No-op when omitted — preserves behavior for chat / send-to / pulse /
   * artifact viewers. The Documentation page passes [rehype-slug] via
   * its DocsMarkdown wrapper so heading anchor IDs are emitted only on
   * the docs surface.
   */
  extraRehypePlugins?: any[];
}

export const ArtifactoryMarkdown: React.FC<ArtifactoryMarkdownProps> = ({
  content,
  size = 'medium',
  className = '',
  extraRehypePlugins,
}) => {
  const { darkMode } = useTheme();
  
  // Size-based classes
  const sizeClasses = {
    small: 'text-xs prose-xs',
    medium: 'text-sm prose-sm',
    large: 'text-base prose-base'
  };
  
  // Create components with proper styling
  const components = {
    // Headings with proper hierarchy and spacing
    h1: ({node, ...props}) => <h1 className="text-2xl font-bold mt-6 mb-4" {...props} />,
    h2: ({node, ...props}) => <h2 className="text-xl font-bold mt-5 mb-3" {...props} />,
    h3: ({node, ...props}) => <h3 className="text-lg font-bold mt-4 mb-2" {...props} />,
    h4: ({node, ...props}) => <h4 className="text-base font-bold mt-3 mb-2" {...props} />,
    h5: ({node, ...props}) => <h5 className="text-sm font-bold mt-2 mb-1" {...props} />,
    h6: ({node, ...props}) => <h6 className="text-xs font-bold mt-2 mb-1" {...props} />,
    
    // Paragraphs with proper spacing
    p: ({node, ...props}) => <p className="mb-4 leading-relaxed" {...props} />,
    
    // Lists with proper indentation and spacing
    ul: ({node, ordered, ...props}) => <ul className="list-disc pl-6 mb-4" {...props} />,
    ol: ({node, ordered, ...props}) => <ol className="list-decimal pl-6 mb-4" {...props} />,
    li: ({node, ordered, checked, ...props}) => <li className="mb-2" {...props} />,
    
    // Code blocks with better styling. ```mermaid fences render as
    // ACTUAL diagrams via MermaidBlock — so a document artifact whose
    // content is a mermaid block (e.g. created via the persona-chat
    // generate_mermaid_diagram → create_artifacts pipeline) shows the
    // rendered diagram by default on the artifact page, not source.
    code: ({node, inline, className, children, ...props}) => {
      const langMatch = /language-(\w+)/.exec(className || '');
      if (!inline && langMatch?.[1] === 'mermaid') {
        // Walk nested React elements to recover the raw text — when
        // rehype-highlight runs (even with ignore=['mermaid']), the
        // code-block children may arrive as `<span>foo</span>` nodes
        // rather than raw strings. Shallow Children.toArray returns
        // empty for that shape and MermaidBlock would render nothing.
        const extractText = (c: any): string => {
          if (typeof c === 'string') return c;
          if (typeof c === 'number') return String(c);
          if (Array.isArray(c)) return c.map(extractText).join('');
          if (React.isValidElement(c)) {
            return extractText((c.props as any).children);
          }
          return '';
        };
        const code = extractText(children).replace(/\n$/, '');
        return <MermaidBlock code={code} darkMode={darkMode}>{children}</MermaidBlock>;
      }
      return inline ? (
        <code className={`px-1 py-0.5 rounded font-mono text-sm ${darkMode ? 'bg-gray-800 text-gray-100' : 'bg-gray-200 text-gray-800'} ${className}`} {...props}>
          {children}
        </code>
      ) : (
        <pre className={`p-3 rounded-md overflow-auto mb-4 ${darkMode ? 'bg-gray-800 text-gray-100' : 'bg-gray-200 text-gray-800'}`}>
          <code className={`font-mono text-sm ${className}`} {...props}>
            {children}
          </code>
        </pre>
      );
    },
    
    // Blockquotes with distinctive styling
    blockquote: ({node, ...props}) => (
      <blockquote className={`pl-4 border-l-4 ${darkMode ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'} italic my-4`} {...props} />
    ),
    
    // Tables with better styling
    table: ({node, ...props}) => (
      <div className="overflow-x-auto mb-4">
        <table className={`min-w-full ${darkMode ? 'border-gray-700' : 'border-gray-300'} border`} {...props} />
      </div>
    ),
    thead: ({node, ...props}) => <thead className={darkMode ? 'bg-gray-800' : 'bg-gray-100'} {...props} />,
    tbody: ({node, ...props}) => <tbody className="divide-y divide-gray-200 dark:divide-gray-700" {...props} />,
    tr: ({node, ...props}) => <tr className={darkMode ? 'border-gray-700' : 'border-gray-300'} {...props} />,
    th: ({node, ...props}) => <th className="px-4 py-2 text-left font-medium" {...props} />,
    td: ({node, ...props}) => <td className="px-4 py-2" {...props} />,
    
    // Strong and emphasis with proper styling
    strong: ({node, ...props}) => <strong className="font-bold" {...props} />,
    em: ({node, ...props}) => <em className="italic" {...props} />,
    
    // Links with proper styling
    a: ({node, ...props}) => (
      <a 
        className={`text-blue-600 dark:text-blue-400 hover:underline`} 
        target="_blank" 
        rel="noopener noreferrer" 
        {...props} 
      />
    ),
    
    // Horizontal rule
    hr: ({node, ...props}) => <hr className={`my-6 ${darkMode ? 'border-gray-700' : 'border-gray-300'}`} {...props} />,
  };
  
  return (
    <ReactMarkdown
      className={`prose max-w-none dark:prose-invert ${sizeClasses[size]} ${className}`}
      remarkPlugins={[remarkGfm]}
      // ignoreMissing + explicit `ignore: ['mermaid']` so rehype-highlight
      // doesn't throw "Unknown language: mermaid" when our custom `code`
      // component has already claimed mermaid blocks for MermaidBlock
      // rendering. Mirrors MessageBubble's setup.
      rehypePlugins={[
        [
          rehypeHighlight as any,
          {
            ignoreMissing: true,
            ignore: ['mermaid'],
            subset: false,
            languages: {},
          },
        ],
        ...(extraRehypePlugins ?? []),
      ]}
      components={components}
    >
      {autoFenceMermaid(content.trim())}
    </ReactMarkdown>
  );
};
